import { zodResolver } from '@hookform/resolvers/zod';
import {
  type FieldValues,
  type Path,
  type Resolver,
  useForm,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import type { z } from 'zod';
import { ApiError } from '../api/client';

/**
 * Forms validate with the same zod schema the API uses (ADR 0011). Server-side field errors
 * (VALIDATION_FAILED with `details.issues`) are put back onto the fields.
 */
export function useZodForm<S extends z.ZodType<FieldValues, FieldValues>>(
  schema: S,
  props: Omit<UseFormProps<z.input<S>, unknown, z.output<S>>, 'resolver'> = {},
): UseFormReturn<z.input<S>, unknown, z.output<S>> {
  // The generic schema type defeats zodResolver's overloads; the cast restates what it returns.
  const resolver = zodResolver(schema as never) as unknown as Resolver<z.input<S>, unknown, z.output<S>>;
  return useForm<z.input<S>, unknown, z.output<S>>({ resolver, ...props });
}

/** `register()` + the field's current error — spread onto a `@pmai/ui` field. */
export function bind<T extends FieldValues>(form: UseFormReturn<T, unknown, FieldValues>, name: Path<T>) {
  const error = form.formState.errors[name]?.message;
  return { ...form.register(name), error: typeof error === 'string' ? error : undefined };
}

/**
 * `bind` for a select whose options arrive later (customers, projects, products): controlled by
 * the form value, so the chosen option shows once the list is there.
 */
export function bindSelect<T extends FieldValues>(
  form: UseFormReturn<T, unknown, FieldValues>,
  name: Path<T>,
) {
  const value: unknown = form.watch(name);
  return { ...bind(form, name), value: typeof value === 'string' ? value : '' };
}

/** Maps server field issues onto the form; returns true when every issue found its field. */
export function applyFieldErrors<T extends FieldValues>(
  form: UseFormReturn<T, unknown, FieldValues>,
  error: unknown,
): boolean {
  if (!(error instanceof ApiError) || error.code !== 'VALIDATION_FAILED') return false;
  const fields = new Set(Object.keys(form.getValues()));
  const issues = error.fieldIssues;
  for (const issue of issues) {
    if (fields.has(issue.path))
      form.setError(issue.path as Path<T>, { type: 'server', message: issue.message });
  }
  return issues.length > 0 && issues.every((i) => fields.has(i.path));
}
